import styles from './EddingGameCard.module.scss';
import { Button, Form, Modal, Radio, RadioChangeEvent, Tag, message } from 'antd';
import {
  Input as CustomInput,
  Layout,
  MultySelect,
  Select,
  Textarea,
  Button as CustomButton,
  Upload,
} from '../..';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { IGameCard, CardTypes } from '../../../types/IGameCard';
import { availabilityGroup, gameCardType, initialAnswersState } from './config';
import { createGameCard, editGameCard } from '../../../store/gamesSlice';
import { useNavigate } from 'react-router-dom';
import useGames from '../../../lib/hooks/useGames';
import Answers from './components/Answers';
import { TLanguage } from '../../../types';
import { VIEW_GAME_CARD_PAGE } from '../../../lib';

const errorMessage = 'Required field';

const EddingGameCard = ({
  logOut,
  action,
  card,
  loading,
}: {
  logOut: () => void;
  action: 'edit' | 'add';
  card: IGameCard;
  loading?: boolean;
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [description, setDescription] = useState<{ eng: string; ru: string }>({
    eng: card?.eng?.description,
    ru: card?.ru?.description,
  });
  const [cardType, setCardType] = useState<CardTypes | null>(card?.type || null);
  const [vaccineCardId, setVaccineCardId] = useState<string | null>(card?.vaccineCardId || null);
  const [musicName, setMusicName] = useState<string | null>(card?.musicQuizTrackName || '');
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [isVaccineCardModalOpen, setIsVaccineCardModalOpen] = useState(false);
  const [isAnswersModalOpen, setIsAnswersModalOpen] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState<string>(
    card?.eng?.answers
      ? (card?.eng?.answers?.findIndex(i => i.isCorrectAnswer) + 1).toString()
      : '1',
  );
  const { onGetGamesCard, gameCards } = useGames();
  const [vaccineCards, setVaccineCards] = useState<IGameCard[] | null>(null);
  const [answers, setAnswers] = useState<any>(
    card?.eng?.answers
      ? {
          eng: card?.eng?.answers?.reduce((acc, obj, index) => {
            acc[`answer${index + 1}`] = obj.value;
            return acc;
          }, {}),
          ru: card?.ru?.answers?.reduce((acc, obj, index) => {
            acc[`answer${index + 1}`] = obj.value;
            return acc;
          }, {}),
        }
      : initialAnswersState,
  );

  const onChangeType = (value: CardTypes) => {
    setCardType(value);
  };

  useEffect(() => {
    onChangeType(card?.type);
    setCardType(card?.type);
    setDescription({
      eng: card?.eng?.description,
      ru: card?.ru?.description,
    });
    setVaccineCardId(card?.vaccineCardId);
    setMusicName(card?.musicQuizTrackName);
    setCorrectAnswer((card?.eng?.answers?.findIndex(i => i.isCorrectAnswer) + 1).toString());
    setAnswers(
      card?.eng?.answers
        ? {
            eng: card?.eng?.answers?.reduce((acc, obj, index) => {
              acc[`answer${index + 1}`] = obj.value;
              return acc;
            }, {}),
            ru: card?.ru?.answers?.reduce((acc, obj, index) => {
              acc[`answer${index + 1}`] = obj.value;
              return acc;
            }, {}),
          }
        : initialAnswersState,
    );
  }, [card]);

  useEffect(() => {
    onGetGamesCard();
  }, []);

  useEffect(() => {
    const vaccines = gameCards.filter(i => i.isVaccine);
    !!gameCards?.length && setVaccineCards(vaccines);
  }, [gameCards]);

  const onChangeDescription = (lang: TLanguage) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(prev => ({ ...prev, [lang]: e?.target?.value }));
  };

  const onChooseVaccineCard = () => {
    setIsVaccineCardModalOpen(false);
  };

  const onSaveAnswers = () => {
    form.validateFields().then(values => {
      const eng = {};
      const ru = {};

      for (const key in values) {
        if (key.endsWith('_eng')) {
          eng[key.replace('_eng', '')] = values[key];
        } else if (key.endsWith('_ru')) {
          ru[key.replace('_ru', '')] = values[key];
        }
      }

      setAnswers({ eng, ru });
      setIsAnswersModalOpen(false);
    });
  };

  const onChangeCorrectAnswer = (e: RadioChangeEvent) => {
    setCorrectAnswer(e.target.value);
  };

  const onCreateGameCard = async (values: any) => {
    setCreateLoading(true);

    const payload = {
      ...values,
      isVirus: cardType === 'virus',
      isVaccine: cardType === 'vaccine',
      ...(cardType === 'quiz' || cardType === 'musicQuiz'
        ? { declineResultCard: Number(values?.declineResultCard) }
        : {}),
      ...(cardType !== 'vaccine'
        ? {
            resultCard: Number(values?.resultCard),
          }
        : {}),
      ...(cardType === 'musicQuiz'
        ? {
            musicQuizTrackName: musicName,
          }
        : {}),
      likesCount: 0,

      ...(!!vaccineCardId
        ? {
            vaccineCardId,
          }
        : {}),
      eng: {
        description: description.eng,
        ...(!!answers && !!Object.keys(answers.eng).length
          ? {
              answers:
                correctAnswer !== 'NaN' // Need a fix @todo
                  ? Object.values(answers.eng).map((i, idx) => ({
                      value: i,
                      isCorrectAnswer: (idx + 1).toString() === correctAnswer,
                    }))
                  : undefined,
            }
          : {}),
      },
      ru: {
        description: description.ru,
        ...(!!answers && !!Object.keys(answers.ru).length
          ? {
              answers:
                correctAnswer !== 'NaN' // Need a fix @todo
                  ? Object.values(answers.ru).map((i, idx) => ({
                      value: i,
                      isCorrectAnswer: (idx + 1).toString() === correctAnswer,
                    }))
                  : undefined,
            }
          : {}),
      },
    };

    if (action === 'add') {
      const response = await dispatch(createGameCard({ ...payload }));
      message.success('Game card created successfully!');
      navigate(`${VIEW_GAME_CARD_PAGE}?id=${response.payload.data?._id}`);
    } else if (action === 'edit') {
      const response = await dispatch(
        editGameCard({
          ...payload,
          _id: card?._id,
        }),
      );
      if (response.payload.data.status === 200) {
        message.success('Game card edited successfully!');
        navigate(`${VIEW_GAME_CARD_PAGE}?id=${card?._id}`);
      } else {
        message.warning('Something went wrong. Try again later.');
      }
    }

    setCreateLoading(false);
  };

  const onViewCard = () => {
    navigate(`${VIEW_GAME_CARD_PAGE}?id=${card?._id}`);
  };

  const onChangeVaccine = (e: RadioChangeEvent) => {
    setVaccineCardId(e.target.value);
  };

  return (
    <Layout
      logOut={logOut}
      title={
        <div className={styles.layoutTitle}>
          <div>{action === 'add' ? 'Create game card' : 'Edit game card'}</div>
          {action === 'edit' && (
            <CustomButton label={'View games card'} bg="#000" color="#fff" onClick={onViewCard} />
          )}
        </div>
      }
      loading={loading}
    >
      {!loading && (
        <Form
          style={{ maxWidth: '700px' }}
          name="create_game_card"
          autoComplete="off"
          onFinish={onCreateGameCard}
          initialValues={card}
        >
          <CustomInput
            name="indexName"
            placeholder="Enter index of the card from Notion`s list"
            label="Index"
            errorMessage={errorMessage}
            size="middle"
            type="text"
          />
          <Select
            name="type"
            options={gameCardType.eng}
            label="Type"
            placeholder="Choose card type"
            errorMessage={errorMessage}
            onChange={onChangeType}
          />
          {cardType && (
            <>
              <div>
                <Textarea
                  name="eng_description"
                  label={
                    <span>
                      Description <Tag color="#00ADB5">EN</Tag>
                    </span>
                  }
                  defaultValue={description.eng}
                  onChangeValue={onChangeDescription('eng')}
                />
                <Textarea
                  name="ru_description"
                  label={
                    <span>
                      Описание <Tag color="#87d068">RU</Tag>
                    </span>
                  }
                  defaultValue={description.ru}
                  onChangeValue={onChangeDescription('ru')}
                />
              </div>

              <MultySelect
                name="availabilityGroup"
                options={availabilityGroup.eng}
                errorMessage={errorMessage}
                label="Availability group"
                placeholder="Choose availability group"
              />
            </>
          )}
          {/* {cardType && (cardType !== 'vaccine' && cardType !== 'fail') && ( */}
          {/* export type CardTypes =
            | 'fortune'
            | 'trump'
            | 'fail'
            | 'truth'
            | 'dare'
            | 'virus'
            | 'vaccine'
            | 'competition'
            | 'quiz'
            | 'musicQuiz'; */}

          {[
            'fail',
            'fortune',
            'truth',
            'quiz',
            'dare',
            'competition',
            'quiz',
            'musicQuiz',
          ].includes(cardType) && (
            <CustomInput
              name="resultCard"
              placeholder="Enter points awarded per card"
              label="Points awarded"
              errorMessage={errorMessage}
              size="middle"
              type="number"
            />
          )}
          {['truth', 'dare', 'quiz', 'musicQuiz'].includes(cardType) && (
            <CustomInput
              name="declineResultCard"
              placeholder="Enter points for refusing to complete a task"
              label="Points for refusing"
              errorMessage={errorMessage}
              size="middle"
              type="number"
            />
          )}

          {cardType === 'musicQuiz' && (
            <div className={styles.vaccineCardWrap}>
              <div>Music quiz track name:</div>
              <Upload
                value={musicName}
                type={'audio'}
                setValue={setMusicName}
                acceptFile={{
                  'audio/mp3': ['.mp3'],
                  'audio/mpeg': ['.mpeg'],
                }}
                withDeleteButton
              />
            </div>
          )}

          {['quiz', 'musicQuiz'].includes(cardType) && (
            <Answers
              answers={answers}
              correctAnswer={correctAnswer}
              setIsAnswersModalOpen={setIsAnswersModalOpen}
              isAnswersModalOpen={isAnswersModalOpen}
              onSaveAnswers={onSaveAnswers}
              form={form}
              onChangeCorrectAnswer={onChangeCorrectAnswer}
            />
          )}

          {cardType === 'virus' && (
            <div className={styles.vaccineCardWrap}>
              <div>Vaccine card:</div>
              <div>
                {!!vaccineCardId && (
                  <div style={{ marginBottom: 8 }}>
                    {vaccineCards?.find(i => i._id === vaccineCardId)?.eng?.description}
                  </div>
                )}
                <Button
                  style={{ background: '#FFCC70', color: '#000' }}
                  onClick={() => setIsVaccineCardModalOpen(true)}
                  block
                >
                  {!!vaccineCardId ? 'Edit vaccine card' : 'Choose vaccine card'}
                </Button>
                <Modal
                  title={
                    !vaccineCards?.length
                      ? 'No vaccine cards have been created.'
                      : 'Choose vaccine card:'
                  }
                  open={isVaccineCardModalOpen}
                  onOk={onChooseVaccineCard}
                  onCancel={() => setIsVaccineCardModalOpen(false)}
                >
                  <Radio.Group
                    onChange={onChangeVaccine}
                    value={vaccineCardId}
                    className={styles.answers}
                  >
                    {!!vaccineCards?.length &&
                      vaccineCards?.map((i, idx) => (
                        <Radio value={i._id} key={idx}>
                          {i?.eng?.description}
                        </Radio>
                      ))}
                  </Radio.Group>
                  <div></div>
                </Modal>
              </div>
            </div>
          )}
          {cardType && (
            <div style={{ marginTop: '20px' }}>
              <CustomButton
                label={action === 'add' ? 'Create' : 'Save'}
                htmlType="submit"
                bg="#000"
                color="#fff"
                loading={createLoading}
              />
            </div>
          )}
        </Form>
      )}
    </Layout>
  );
};

export default EddingGameCard;
